import React from "react";
import { Navigate, useLocation } from "react-router-dom";

import useAuth from "../hooks/useAuth";
import useSidebarMenu from "../hooks/useSidebarMenu";
import { getHomeForRole, isPathAllowedForRole } from "../utils/links";
import logger from "../utils/logger";

/**
 * Guards every signed-in page: requires a session, and only lets a role open
 * the pages of its sidebar menu (from Intelliconvo, else ROLE_MENUS in
 * utils/links.js). Anything else — including a URL typed by hand — goes back
 * to the role's home page.
 *
 * This only shapes the UI; the backend checks every API call itself.
 */
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, role } = useAuth();
  const { menu, isLoading } = useSidebarMenu();
  const { pathname } = useLocation();

  if (!isAuthenticated) {
    logger.info(`[ProtectedRoute] No session — redirecting ${pathname} to login.`);
    return <Navigate to="/login" />;
  }

  // Decide only once the menu is known, so a page is never blocked by the wrong menu.
  if (isLoading) {
    return null;
  }

  if (!isPathAllowedForRole(role, pathname, menu)) {
    const home = getHomeForRole(role, menu);
    if (!home) {
      logger.warn(`[ProtectedRoute] Session has no TalentEngine role (stored: ${role || "none"}) — redirecting to login.`);
      return <Navigate to="/login" replace />;
    }
    logger.warn(`[ProtectedRoute] Role "${role}" may not open ${pathname} — redirecting to ${home}.`);
    return <Navigate to={home} replace />;
  }

  return children;
};

export default ProtectedRoute;
